import { Box, Flex, HStack, Image, SimpleGrid, Text, VStack, Separator } from '@chakra-ui/react'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { useEffect, useMemo, useRef, useState } from 'react'

type SuccessStatsCardStat = {
  title?: string
  value: string
  valueFontSize?: string
  valueColor?: string
  bgColor?: string
  bgImage?: string
  customerLogo?: string
  showSocialProofContent?: boolean
  socialProofContentTitle?: string
  socialProofContentDescription?: string
}

export const SuccessStats: React.FC = () => {
  const { getBreakpointValue } = useBreakpointContext()
  const isTablet = getBreakpointValue([false, false, true, false, false])
  return (
    <VStack align={'stretch'} gap={4} height={'full'} justify={'stretch'} width={'full'}>
      <SimpleGrid columns={getBreakpointValue(['1', '1', '2', '3'])} gap={4} width={'full'}>
        <SuccessStatsItem
          bgColor={'green.muted/50'}
          bgImage={'/assets/graphics/social/ag1-social-2.png'}
          customerLogo={'/assets/graphics/client-logos/ag1-logo.svg'}
          showSocialProofContent={true}
          socialProofContentDescription={'From Tim Ferris, Hugh Jackman at $1 BILLION valuation'}
          socialProofContentTitle={'$115,000,000 Raised'}
          staggerMs={0}
          title={'Follow-On Funding'}
          value={'$20 BILLION'}
          valueColor={'green.fg'}
          valueFontSize={'5xl'}
          rotationStats={[
            {
              value: '15 IPOs',
              valueFontSize: '8xl',
              valueColor: 'red.fg',
              bgColor: 'red.muted/25',
              bgImage: '/assets/graphics/social/owlet-social-1.png',
              customerLogo: '/assets/graphics/client-logos/owlet-logo.svg',
              showSocialProofContent: true,
              socialProofContentTitle: '$178,000,000 Raised',
            },
          ]}
        />
        <VStack gap={4} width={'full'}>
          <HStack gap={4} width={'full'}>
            <SuccessStatsItem
              staggerMs={300}
              title={'Raised'}
              value={'$1 BILLION'}
              valueFontSize={getBreakpointValue(['3xl', '4xl', '4xl', '3xl', '4xl'])}
              rotationStats={[
                {
                  title: 'Startups',
                  value: '1,000,000',
                  valueFontSize: getBreakpointValue(['2xl', '3xl', '3xl', '2xl', '3xl']),
                  valueColor: 'blue.fg',
                  bgColor: 'blue.fg/50',
                },
              ]}
            />
            <SuccessStatsItem
              bgColor={'pink.muted/50'}
              staggerMs={700}
              title={'Verified Experts'}
              value={'20,000'}
              valueColor={'pink.fg'}
              rotationStats={[
                {
                  title: 'On an Expert Call',
                  value: 'EVERY 90 SECONDS',
                  valueFontSize: getBreakpointValue(['2xl', '3xl', '3xl', '2xl', '3xl']),
                  valueColor: 'orange.fg',
                  bgColor: 'orange.muted/50',
                },
              ]}
            />
          </HStack>
          <SuccessStatsItem
            bgColor={'orange.muted/50'}
            bgImage={'/assets/graphics/social/instacart-brand.png'}
            customerLogo={'/assets/graphics/client-logos/instacart-logo.svg'}
            showSocialProofContent={true}
            socialProofContentTitle={'$2,740,000,000 Raised'}
            staggerMs={1100}
            title={'Site Launches'}
            value={'237,000'}
            valueColor={'colorPalette.fg'}
            valueFontSize={'5xl'}
            rotationStats={[
              {
                title: 'Customers Acquired',
                value: '23 Million',
                valueFontSize: '5xl',
                valueColor: 'red.fg',
                bgColor: 'red.muted/50',
              },
            ]}
          />
        </VStack>
        {isTablet ? (
          <SuccessStatsItem
            bgColor={'purple.muted/50'}
            bgImage={'/assets/graphics/social/unyq.jpeg'}
            customerLogo={'/assets/graphics/client-logos/unyq-logo.svg'}
            showSocialProofContent={true}
            staggerMs={500}
            title={'Raised'}
            value={'$35M'}
            valueColor={'colorPalette.fg'}
            valueFontSize={'5xl'}
            rotationStats={[
              {
                title: '5-Star Reviews',
                value: '73,000',
                valueFontSize: '4xl',
                valueColor: 'green.fg',
                bgColor: 'green.muted/50',
              },
            ]}
          />
        ) : null}
        <HStack gap={4} width={'full'}>
          {!isTablet ? (
            <SuccessStatsItem
              bgColor={'purple.muted/50'}
              bgImage={'/assets/graphics/social/unyq.jpeg'}
              customerLogo={'/assets/graphics/client-logos/unyq-logo.svg'}
              showSocialProofContent={true}
              staggerMs={500}
              title={'Raised'}
              value={'$35M'}
              valueColor={'colorPalette.fg'}
              valueFontSize={'5xl'}
              rotationStats={[
                {
                  title: '5-Star Reviews',
                  value: '73,000',
                  valueFontSize: '4xl',
                  valueColor: 'green.fg',
                  bgColor: 'green.muted/50',
                },
              ]}
            />
          ) : null}
          <VStack gap={4} width={'full'}>
            <SuccessStatsItem
              bgColor={'blue.muted/50'}
              staggerMs={900}
              title={'Answers'}
              value={'17,000'}
              valueColor={'blue.fg'}
              valueFontSize={'3xl'}
              rotationStats={[
                { title: 'Testimonials', value: '30,000', valueColor: 'blue.fg', bgColor: 'blue.muted/50' },
              ]}
            />
            <SuccessStatsItem
              bgColor={'pink.muted/50'}
              staggerMs={1300}
              title={'Masterclasses'}
              value={'1,000'}
              valueColor={'pink.fg'}
              valueFontSize={'3xl'}
              rotationStats={[
                {
                  title: 'Verified Experts',
                  value: '20,000',
                  valueFontSize: '3xl',
                  valueColor: 'pink.fg',
                  bgColor: 'pink.muted/50',
                },
              ]}
            />
          </VStack>
        </HStack>
      </SimpleGrid>
    </VStack>
  )
}

type SuccessStatsItemProps = {
  title?: string
  value?: string
  valueFontSize?: string
  valueColor?: string
  bgColor?: string
  bgImage?: string
  customerLogo?: string
  showSocialProofContent?: boolean
  socialProofContentTitle?: string
  socialProofContentDescription?: string
  rotationStats?: SuccessStatsCardStat[]
  staggerMs?: number
}

const getRandomDelayMs = (minMs: number, maxMs: number) => {
  return Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs
}

const SuccessStatsItem: React.FC<SuccessStatsItemProps> = (props: SuccessStatsItemProps) => {
  const {
    title,
    value,
    valueFontSize = '4xl',
    valueColor = 'colorPalette.fg',
    bgColor = 'purple.muted/50',
    bgImage,
    customerLogo,
    showSocialProofContent = false,
    socialProofContentTitle,
    socialProofContentDescription,
    rotationStats = [],
    staggerMs = 0,
  } = props

  const normalizedStats = useMemo<SuccessStatsCardStat[]>(() => {
    const baseStat: SuccessStatsCardStat = {
      title,
      value,
      valueFontSize,
      valueColor,
      bgColor,
      bgImage,
      customerLogo,
      showSocialProofContent,
      socialProofContentTitle,
      socialProofContentDescription,
    }

    return [baseStat, ...rotationStats].map(stat => ({
      ...stat,
      valueFontSize: stat.valueFontSize ?? '4xl',
      valueColor: stat.valueColor ?? 'colorPalette.border',
      bgColor: stat.bgColor ?? 'purple.muted/50',
      showSocialProofContent: stat.showSocialProofContent ?? false,
    }))
  }, [
    bgColor,
    bgImage,
    customerLogo,
    rotationStats,
    showSocialProofContent,
    socialProofContentDescription,
    socialProofContentTitle,
    title,
    value,
    valueColor,
    valueFontSize,
  ])
  const [frontIndex, setFrontIndex] = useState(0)
  const [backIndex, setBackIndex] = useState(() => (normalizedStats.length > 1 ? 1 : 0))
  const [isFrontVisible, setIsFrontVisible] = useState(true)
  const activeIndexRef = useRef(0)
  const isFrontVisibleRef = useRef(true)
  const cycleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const postFlipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (normalizedStats.length <= 1) {
      setFrontIndex(0)
      setBackIndex(0)
      setIsFrontVisible(true)
      activeIndexRef.current = 0
      isFrontVisibleRef.current = true
      return
    }

    const animationDurationMs = 700
    const minRotationDelayMs = 600
    const maxRotationDelayMs = 7000

    const clearTimers = () => {
      if (cycleTimeoutRef.current) {
        clearTimeout(cycleTimeoutRef.current)
      }
      if (postFlipTimeoutRef.current) {
        clearTimeout(postFlipTimeoutRef.current)
      }
    }

    setFrontIndex(0)
    setBackIndex(1)
    setIsFrontVisible(true)
    activeIndexRef.current = 0
    isFrontVisibleRef.current = true

    const scheduleNextCycle = (delayMs: number) => {
      cycleTimeoutRef.current = setTimeout(() => {
        const nextIndex = (activeIndexRef.current + 1) % normalizedStats.length

        if (isFrontVisibleRef.current) {
          setBackIndex(nextIndex)
          setIsFrontVisible(false)
          isFrontVisibleRef.current = false
        } else {
          setFrontIndex(nextIndex)
          setIsFrontVisible(true)
          isFrontVisibleRef.current = true
        }

        postFlipTimeoutRef.current = setTimeout(() => {
          activeIndexRef.current = nextIndex
          scheduleNextCycle(getRandomDelayMs(minRotationDelayMs, maxRotationDelayMs))
        }, animationDurationMs)
      }, delayMs)
    }

    scheduleNextCycle(getRandomDelayMs(minRotationDelayMs, maxRotationDelayMs) + staggerMs)

    return () => {
      clearTimers()
    }
  }, [normalizedStats, staggerMs])

  const frontStat = normalizedStats[frontIndex]
  const backStat = normalizedStats[backIndex]

  const { getBreakpointValue } = useBreakpointContext()

  if (!frontStat || !backStat) {
    return null
  }

  const renderCardContent = (stat: SuccessStatsCardStat) => (
    <VStack
      alignItems={'center'}
      bgColor={stat.bgColor}
      borderRadius={'lg'}
      boxShadow={'inset 0 1px 0 rgba(255, 255, 255, 0.25)'}
      display={'flex'}
      flexDirection={'column'}
      gap={0}
      height={'full'}
      justifyContent={'center'}
      minHeight={'220px'}
      width={'full'}
    >
      <VStack
        alignItems={'center'}
        gap={0}
        height={'full'}
        justifyContent={'center'}
        padding={4}
        width={'full'}
      >
        <Text
          color={stat.valueColor}
          fontSize={stat.valueFontSize}
          fontWeight={'700'}
          letterSpacing={'-0.05em'}
          lineHeight={'shorter'}
          textStyle={'labelLg'}
          textAlign={'center'}
          // textShadow={'0 0 10px rgba(0, 0, 0, 0.5)'}
          opacity={1}
        >
          {stat.value}
        </Text>
        <Text
          textAlign={'center'}
          textStyle={getBreakpointValue(['labelLg', 'labelXl', 'labelXl', 'labelMd', 'labelXl'])}
          color={'red.fg'}
          // textShadow={'0 0 8px rgba(0, 0, 0, 0.5)'}
          opacity={1}
        >
          {stat.title}
        </Text>
      </VStack>
      {stat.showSocialProofContent && (
        <HStack
          align={'center'}
          bgColor={'colorPalette.bg/75'}
          borderBottomRadius={'lg'}
          gap={2}
          justify={'center'}
          padding={4}
          width={'full'}
          css={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          {stat.customerLogo && (
            <Box>
              <Image
                alt={'customer logo'}
                filter={{ _light: 'invert(1)', _dark: 'invert(0)' }}
                maxWidth={'80px'}
                objectFit={'contain'}
                src={stat.customerLogo}
              />
            </Box>
          )}
          <Separator
            borderColor={'red.fg'}
            display={stat.socialProofContentTitle ? 'block' : 'none'}
            opacity={0.5}
          />
          <VStack align={'flex-start'} gap={0}>
            <Text color={'red.fg'} opacity={1} textStyle={'labelMd'}>
              {stat.socialProofContentTitle}
            </Text>
          </VStack>
        </HStack>
      )}
    </VStack>
  )

  const frontBackground = frontStat.bgImage
    ? `linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.8) 80%), url(${frontStat.bgImage}) top center/cover no-repeat`
    : 'none'
  const backBackground = backStat.bgImage
    ? `linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.8) 80%), url(${backStat.bgImage}) top center/cover no-repeat`
    : 'none'

  return (
    <Flex
      align={'center'}
      background={'transparent'}
      borderRadius={'lg'}
      boxShadow={'sm'}
      direction={'column'}
      height={'full'}
      justify={'center'}
      minHeight={'220px'}
      width={'full'}
      css={{
        perspective: '1200px',
      }}
    >
      <Box
        height={'full'}
        minHeight={'220px'}
        position={'relative'}
        transform={isFrontVisible ? 'rotateY(0deg)' : 'rotateY(180deg)'}
        transition={'transform 700ms ease-in-out'}
        width={'full'}
        css={{
          transformStyle: 'preserve-3d',
        }}
      >
        <Box
          background={frontBackground}
          backgroundSize={'cover'}
          borderRadius={'lg'}
          inset={0}
          overflow={'hidden'}
          position={'absolute'}
          css={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(0deg) translateZ(1px)',
            transformStyle: 'preserve-3d',
          }}
        >
          {renderCardContent(frontStat)}
        </Box>
        <Box
          background={backBackground}
          backgroundSize={'cover'}
          borderRadius={'lg'}
          inset={0}
          overflow={'hidden'}
          position={'absolute'}
          css={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg) translateZ(1px)',
            transformStyle: 'preserve-3d',
          }}
        >
          {renderCardContent(backStat)}
        </Box>
      </Box>
    </Flex>
  )
}
